// convex/lib/config/domains/system.ts

import { validatorCompany } from "../../../companies";
import { validatorBranch } from "../../../companies/branches";
import { validatorWaitlist } from "../../../system/waitlist";
import { validatorUsers } from "../../../system/users";
import { validatorAuditLog } from "../../../system/audit_logs";
import { validatorLicense } from "../../../system/licensing";
import { validatorCompanySubscription } from "../../../companies/subscriptions";
import { validatorSubscriptionPlan } from "../../../system/plans";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// SYSTEM DOMAIN - Status Values for i18n
// ============================================================================

export const BRANCH_STATUSES = ["active", "inactive"] as const;
export const WAITLIST_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "contacted",
] as const;

export const systemDomain: DomainGroup = {
  id: "system",
  name: "Sistema",
  description: "Configuración y administración del sistema",
  icon: "Settings",
  order: 100,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "system",
    keys: {
      title: "system.title",
      description: "system.description",
    },
  },

  modules: [
    {
      id: "control_panel",
      name: "Panel de Control",
      description: "Gestión avanzada de empresas y suscripciones",
      icon: "LayoutDashboard",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/super/control-panel"],
      navigation: {
        showInNav: false,
        contexts: ["super"],
        navOrder: 1,
        status: "active",
      },

      i18n: {
        namespace: "system.control_panel",
        keys: {
          title: "system.control_panel.title",
          description: "system.control_panel.description",
          longDescription: "system.control_panel.longDescription",
          searchPlaceholder: "system.control_panel.searchPlaceholder",
          emptyState: "system.control_panel.emptyState",
          actions: {
            edit: "system.control_panel.actions.edit",
            view: "system.control_panel.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "companies",
          schema: validatorCompany,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "phone", "taxId"],
          hasStatusIndex: false,
          searchIndex: { name: "search_companies", field: "name" },
        },
        {
          tableName: "company_subscriptions",
          schema: validatorCompanySubscription,
          scope: "company" as TableScope,
          relations: [
            {
              key: "company",
              sourceField: "companyId",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "plan",
              sourceField: "planId",
              schema: validatorSubscriptionPlan,
              tableName: "subscription_plans",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["status"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "subscription_plans" },
        { tableName: "users" },
        { tableName: "branches" },
      ],
    },
    {
      id: "companies",
      name: "Empresas",
      description: "Gestión de empresas y configuración general",
      icon: "Building2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/system/companies", "/super/companies"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 3,
        status: "active",
      },

      i18n: {
        namespace: "system.companies",
        keys: {
          title: "system.companies.title",
          description: "system.companies.description",
          longDescription: "system.companies.longDescription",
          searchPlaceholder: "system.companies.searchPlaceholder",
          emptyState: "system.companies.emptyState",
          actions: {
            create: "system.companies.actions.create",
            edit: "system.companies.actions.edit",
            delete: "system.companies.actions.delete",
            view: "system.companies.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "companies",
          schema: validatorCompany,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "phone", "taxId"],
          hasStatusIndex: false,
          searchIndex: { name: "search_companies", field: "name" },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "branches",
      name: "Sucursales",
      description: "Configuración de sucursales",
      icon: "MapPin",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/branches"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 1,
        status: "active",
      },

      i18n: {
        namespace: "system.branches",
        keys: {
          title: "system.branches.title",
          description: "system.branches.description",
          longDescription: "system.branches.longDescription",
          searchPlaceholder: "system.branches.searchPlaceholder",
          emptyState: "system.branches.emptyState",
          actions: {
            create: "system.branches.actions.create",
            edit: "system.branches.actions.edit",
            delete: "system.branches.actions.delete",
            view: "system.branches.actions.view",
          },
        },
        translatableFields: ["name", "address"],
        translatableEnums: [{ field: "status", values: BRANCH_STATUSES }],
      },

      tables: [
        {
          tableName: "branches",
          schema: validatorBranch,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "company",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "code", "address", "phone"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [{ tableName: "companies" }],
    },
    {
      id: "waitlist",
      name: "Lista de Espera",
      description: "Gestion de solicitudes de acceso y referidos",
      icon: "ClipboardList",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/super/waitlist"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 8,
        status: "active",
      },

      i18n: {
        namespace: "system.waitlist",
        keys: {
          title: "system.waitlist.title",
          description: "system.waitlist.description",
          longDescription: "system.waitlist.longDescription",
          searchPlaceholder: "system.waitlist.searchPlaceholder",
          emptyState: "system.waitlist.emptyState",
          actions: {
            edit: "system.waitlist.actions.edit",
            view: "system.waitlist.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: WAITLIST_STATUSES }],
      },

      tables: [
        {
          tableName: "waitlist",
          schema: validatorWaitlist,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "company", "referralCode"],
          hasStatusIndex: true,
          searchIndex: { name: "search_waitlist", field: "name" },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "audit_logs",
      name: "Registros de Auditoría",
      description: "Registro de cambios y acciones en el sistema",
      icon: "Eye",
      permissions: ["canRead"],
      routes: ["/settings/audit-logs"],
      navigation: {
        showInNav: false,
        contexts: ["settings"],
        navOrder: 98,
        status: "active",
      },
      i18n: {
        namespace: "system.audit_logs",
        keys: {
          title: "system.audit_logs.title",
          description: "system.audit_logs.description",
          longDescription: "system.audit_logs.longDescription",
          searchPlaceholder: "system.audit_logs.searchPlaceholder",
          emptyState: "system.audit_logs.emptyState",
          actions: {
            view: "system.audit_logs.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "audit_logs",
          schema: validatorAuditLog,
          scope: "company" as TableScope,
          relations: [
            {
              key: "user",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["action"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "licensing",
      name: "Licencias",
      description: "Emisión y revocación de licencias on-prem",
      icon: "Key",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/super/licenses"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 7,
        status: "active",
      },
      i18n: {
        namespace: "system.licensing",
        keys: {
          title: "system.licensing.title",
          description: "system.licensing.description",
          longDescription: "system.licensing.longDescription",
          searchPlaceholder: "system.licensing.searchPlaceholder",
          emptyState: "system.licensing.emptyState",
          actions: {
            create: "system.licensing.actions.create",
            view: "system.licensing.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "licenses",
          schema: validatorLicense,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["licenseId", "customerName", "customerEmail"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "deleted_records",
      name: "Papelera",
      description: "Registros eliminados que pueden ser restaurados",
      icon: "Trash2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/trash"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 99,
        status: "active",
      },

      i18n: {
        namespace: "system.deleted_records",
        keys: {
          title: "system.deleted_records.title",
          description: "system.deleted_records.description",
          longDescription: "system.deleted_records.longDescription",
          searchPlaceholder: "system.deleted_records.searchPlaceholder",
          emptyState: "system.deleted_records.emptyState",
          actions: {
            delete: "system.deleted_records.actions.delete",
            restore: "system.deleted_records.actions.restore",
            view: "system.deleted_records.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [],
      readOnlyTables: [{ tableName: "nomenclature" }],
    },
    {
      id: "platformsAndSessions",
      name: "Seguridad",
      description:
        "Restricciones de plataformas y limite de sesiones simultaneas por empresa",
      icon: "Shield",
      permissions: ["canRead", "canUpdate"],
      routes: ["/settings/security"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 2,
        status: "active",
      },

      i18n: {
        namespace: "system.platforms_and_sessions",
        keys: {
          title: "system.platforms_and_sessions.title",
          description: "system.platforms_and_sessions.description",
          longDescription:
            "system.platforms_and_sessions.longDescription",
          searchPlaceholder:
            "system.platforms_and_sessions.searchPlaceholder",
          emptyState: "system.platforms_and_sessions.emptyState",
          actions: {
            edit: "system.platforms_and_sessions.actions.edit",
            view: "system.platforms_and_sessions.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [],
      readOnlyTables: [{ tableName: "companies" }],
    },
  ],
};
